import { TiptapEditor, type TiptapOnCreateCallbackProps } from '@/components/tiptap-editor/tiptap-editor';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { parseCompareJsonInput } from '@/lib/snapshot/compare-documents';
import { exitSnapshotCompare, runDocumentCompare } from '@/lib/snapshot/snapshot-review';
import demoCompareModifiedJSON from '@/tests/demo-contents/demo-compare-modified.json';
import demoCompareOriginalJSON from '@/tests/demo-contents/demo-compare-original.json';
import type { Editor } from '@tiptap/react';
import { useCallback, useMemo, useState } from 'react';

const SAMPLE_DOC_A = JSON.stringify(demoCompareOriginalJSON, null, 2);
const SAMPLE_DOC_B = JSON.stringify(demoCompareModifiedJSON, null, 2);

function exitDocumentComparePreview(editor: Editor): void {
    if (editor.storage.snapshotCompare?.isPreviewing) {
        exitSnapshotCompare(editor);
    }
}

export default function DocumentCompareContent() {
    const [docAText, setDocAText] = useState(SAMPLE_DOC_A);
    const [docBText, setDocBText] = useState(SAMPLE_DOC_B);
    const [editor, setEditor] = useState<Editor | null>(null);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [isComparing, setIsComparing] = useState(false);
    const [isDiffPreviewing, setIsDiffPreviewing] = useState(false);

    const extensionOptions = useMemo(
        () => ({
            snapshotCompare: { enabled: true },
        }),
        [],
    );

    const handleEditorCreate = useCallback(({ editor: ed }: TiptapOnCreateCallbackProps) => {
        setEditor(ed);
    }, []);

    const handleCompare = useCallback(async () => {
        if (!editor) {
            setStatusMessage('Editor is not ready yet.');
            return;
        }

        setIsComparing(true);
        setStatusMessage(null);

        try {
            const jsonA = parseCompareJsonInput(docAText);
            const jsonB = parseCompareJsonInput(docBText);

            exitDocumentComparePreview(editor);
            editor.setEditable(true);

            editor.commands.setContentWithMeta(jsonA);

            const result = await runDocumentCompare(editor, jsonA, jsonB);
            if (!result.ok) {
                setStatusMessage(result.error);
                return;
            }

            editor.setEditable(false);
            setIsDiffPreviewing(true);
            setStatusMessage(`Showing ${result.diffCount} diff${result.diffCount === 1 ? '' : 's'}.`);
        } catch (error) {
            setStatusMessage(error instanceof Error ? error.message : 'Failed to parse JSON.');
        } finally {
            setIsComparing(false);
        }
    }, [docAText, docBText, editor]);

    const handleHideDiff = useCallback(() => {
        if (!editor) {
            return;
        }

        exitDocumentComparePreview(editor);
        editor.setEditable(true);
        setIsDiffPreviewing(false);
        setStatusMessage('Diff hidden.');
    }, [editor]);

    return (
        <div className="flex h-[calc(100svh-49px)] min-h-0 flex-col overflow-hidden">
            <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto overscroll-y-contain p-4">
                <div className="shrink-0">
                    <h1 className="text-xl font-semibold">Document compare (POC)</h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        Paste two clone-related ProseMirror JSON documents and compare them with the local id-based diff.
                    </p>
                </div>

                <div className="grid shrink-0 gap-4 lg:grid-cols-2">
                    <Card className="flex flex-col">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base">Doc A (baseline)</CardTitle>
                            <CardDescription>ProseMirror JSON for the document before changes.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Label
                                htmlFor="doc-a-json"
                                className="sr-only"
                            >
                                Doc A JSON
                            </Label>
                            <Textarea
                                id="doc-a-json"
                                value={docAText}
                                onChange={event => setDocAText(event.target.value)}
                                className="h-48 resize-y font-mono text-xs"
                                disabled={isDiffPreviewing}
                            />
                        </CardContent>
                    </Card>

                    <Card className="flex flex-col">
                        <CardHeader className="pb-3">
                            <CardTitle className="text-base">Doc B (variant)</CardTitle>
                            <CardDescription>ProseMirror JSON for the document after changes.</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <Label
                                htmlFor="doc-b-json"
                                className="sr-only"
                            >
                                Doc B JSON
                            </Label>
                            <Textarea
                                id="doc-b-json"
                                value={docBText}
                                onChange={event => setDocBText(event.target.value)}
                                className="h-48 resize-y font-mono text-xs"
                                disabled={isDiffPreviewing}
                            />
                        </CardContent>
                    </Card>
                </div>

                <div className="flex shrink-0 flex-wrap items-center gap-3">
                    <Button
                        onClick={handleCompare}
                        disabled={isComparing || isDiffPreviewing}
                    >
                        {isComparing ? 'Comparing…' : 'Compare'}
                    </Button>
                    <Button
                        variant="outline"
                        onClick={handleHideDiff}
                        disabled={!isDiffPreviewing}
                    >
                        Hide diff
                    </Button>
                    {statusMessage && <p className="text-muted-foreground text-sm">{statusMessage}</p>}
                </div>

                <Card className="flex min-h-[28rem] shrink-0 flex-col overflow-hidden">
                    <CardHeader className="shrink-0 pb-3">
                        <CardTitle className="text-base">Diff preview</CardTitle>
                        <CardDescription>Snapshot Compare decorations rendered in a non-collaborative editor.</CardDescription>
                    </CardHeader>
                    <CardContent className="min-h-0 flex-1 overflow-hidden p-0">
                        <div className="document-compare-preview h-[min(60vh,720px)] min-h-[24rem] overflow-y-auto border-t">
                            <TiptapEditor
                                content={demoCompareOriginalJSON}
                                isInitialContentLoaded
                                useCollaboration={false}
                                extensionOptions={extensionOptions}
                                onCreate={handleEditorCreate}
                            />
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
